import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { KeyRound, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1=identifier, 2=otp+password
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [busy, setBusy] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot", { identifier: identifier.trim() });
      if (data.dev_otp) setDevOtp(data.dev_otp);
      toast.success(data.message || "OTP sent (check SMS/email)");
      setStep(2);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send OTP");
    } finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirm) return toast.error("Passwords do not match");
    if (newPassword.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { identifier: identifier.trim(), otp: otp.trim(), new_password: newPassword });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md bg-white rounded-3xl p-10 premium-shadow">
        <div className="h-14 w-14 rounded-2xl bg-emerald-600 text-white flex items-center justify-center"><KeyRound className="h-7 w-7" /></div>
        <h1 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">Forgot password</h1>
        <p className="mt-1 text-gray-600 text-sm">{step === 1 ? "We'll send a 6-digit OTP to reset your password." : "Enter the OTP and set your new password."}</p>

        {step === 1 ? (
          <form onSubmit={requestOtp} className="mt-8 space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 tracking-wide">Mobile or Email</label>
              <input data-testid="forgot-identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required
                className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            <button data-testid="forgot-send-otp" disabled={busy || !identifier.trim()} className="w-full py-4 rounded-full bg-emerald-600 text-white font-semibold disabled:opacity-60">
              {busy ? "Sending..." : "Send OTP"}
            </button>
            <div className="text-center text-sm text-gray-600">
              Remember it? <Link to="/login" className="text-emerald-700 font-medium">Sign in</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={submitReset} className="mt-8 space-y-4">
            {devOtp && <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">DEV OTP (SMS/email service not connected): <b className="font-mono tracking-widest">{devOtp}</b></div>}
            <div>
              <label className="text-xs font-semibold text-gray-600 tracking-wide">6-digit OTP</label>
              <input data-testid="forgot-otp" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} required
                className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 font-mono tracking-[0.5em] text-center text-lg" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 tracking-wide">New Password</label>
              <input data-testid="forgot-new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required
                className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 tracking-wide">Confirm Password</label>
              <input data-testid="forgot-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" />
            </div>
            <button data-testid="forgot-submit" disabled={busy} className="w-full py-4 rounded-full bg-emerald-600 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? "Updating..." : (<>Reset password <ArrowRight className="h-4 w-4" /></>)}
            </button>
            <button type="button" onClick={() => { setStep(1); setDevOtp(""); }} className="w-full text-center text-sm text-gray-500 hover:text-gray-800">Wrong number? Start over</button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
